import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '../styles/calendar.css';
import adminAPI from '../utils/api';
import toast from 'react-hot-toast';
import SessionDetailModal from '../components/SessionDetailModal';
import ConfirmationModal from '../components/ConfirmationModal';

const Schedule = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL parameters
  const getInitialDate = () => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsedDate = new Date(dateParam);
      return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    }
    return new Date();
  };

  const getInitialView = () => {
    const viewParam = searchParams.get('view');
    return ['day', 'week', 'month'].includes(viewParam) ? viewParam : 'week';
  };

  const [currentDate, setCurrentDate] = useState(getInitialDate());
  const [view, setView] = useState(getInitialView());
  const [sessions, setSessions] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'duplicate'
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailSession, setDetailSession] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });
  const [realTimeConflicts, setRealTimeConflicts] = useState([]);

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
    '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00',
    '22:00', '23:00',
  ];

  // Update URL parameters when date or view changes
  const updateURL = (newDate, newView) => {
    const params = new URLSearchParams();
    params.set('date', newDate.toISOString().split('T')[0]); // YYYY-MM-DD format
    params.set('view', newView);
    setSearchParams(params);
  };

  // Handle date change with URL update
  const handleDateChange = (newDate) => {
    setCurrentDate(newDate);
    updateURL(newDate, view);
  };

  // Handle view change with URL update
  const handleViewChange = (newView) => {
    setView(newView);
    updateURL(currentDate, newView);
  };

  // Fetch data from API
  const fetchData = async () => {
    try {
      setLoading(true);
      const [sessionsData, coachesData, locationsData] = await Promise.all([
        adminAPI.getSessions(), // Get all sessions
        adminAPI.getCoaches(),
        adminAPI.getLocations()
      ]);

      setSessions(Array.isArray(sessionsData) ? sessionsData : []);
      setCoaches(Array.isArray(coachesData) ? coachesData : (coachesData.results || []));
      setLocations(Array.isArray(locationsData) ? locationsData : (locationsData.results || []));

      // Update detailSession if modal is open and session exists in new data
      if (detailSession && isDetailModalOpen) {
        const updatedSession = sessionsData.find(s => s.id === detailSession.id);
        if (updatedSession) {
          setDetailSession(updatedSession);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentDate]);



  const getWeekDays = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay() + 1); // Start from Monday
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const formatDate = (date) => {
    // Use local timezone to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isTimeSlotInPast = (date, time) => {
    const now = new Date();
    const slotDateTime = new Date(`${formatDate(date)}T${time}:00`);
    return slotDateTime < now;
  };

  const getSessionsForSlot = (date, time) => {
    const dateStr = formatDate(date);
    const timeWithSeconds = time + ':00';
    return sessions.filter(session =>
      session.date === dateStr && session.start_time === timeWithSeconds
    );
  };

  // CRUD Operations
  const handleCreateSession = (date, time) => {
    setSelectedSession({
      date: formatDate(date),
      start_time: time + ':00',
      end_time: (parseInt(time.split(':')[0]) + 1).toString().padStart(2, '0') + ':00:00',
      coach: '',
      location: '',
      max_capacity: 1,
      status: 'available'
    });
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditSession = async (e, session) => {
    e.stopPropagation(); // Prevent event bubbling to avoid opening detail modal

    // Fetch session group information if session has a group_id
    let sessionWithGroup = { ...session };
    if (session.session_group_id) {
      try {
        const groupData = await adminAPI.getSessionGroup(session.id);
        sessionWithGroup.groupSessions = groupData.sessions;
        sessionWithGroup.totalGroupSessions = groupData.total_sessions;
        sessionWithGroup.updatableGroupSessions = groupData.updatable_sessions;
      } catch (error) {
        console.error('Error fetching session group:', error);
        // Continue without group data
      }
    }

    setSelectedSession(sessionWithGroup);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDuplicateSession = (session) => {
    setSelectedSession({
      ...session,
      id: null, // Remove ID for duplication
      date: formatDate(currentDate), // Use current date
      current_bookings: 0,
      status: 'available'
    });
    setModalMode('duplicate');
    setShowModal(true);
  };

  const handleViewSessionDetails = (session) => {
    setDetailSession(session);
    setIsDetailModalOpen(true);
  };

  const handleDeleteSession = (e, sessionId) => {
    e.stopPropagation(); // Prevent event bubbling to avoid opening detail modal

    setConfirmationModal({
      isOpen: true,
      title: 'Supprimer la séance',
      message: 'Êtes-vous sûr de vouloir supprimer cette séance ? Cette action est irréversible.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await adminAPI.deleteSession(sessionId);
          // Update local state immediately
          setSessions(sessions.filter(s => s.id !== sessionId));
          toast.success('Séance supprimée avec succès');
        } catch (error) {
          console.error('Error deleting session:', error);
          // Check if it's actually deleted (status 204)
          if (error.message.includes('204') || error.status === 204) {
            setSessions(sessions.filter(s => s.id !== sessionId));
            toast.success('Séance supprimée avec succès');
          } else {
            toast.error('Erreur lors de la suppression de la séance');
          }
        }
        setConfirmationModal({ ...confirmationModal, isOpen: false });
      }
    });
  };

  const handleSaveSession = async (sessionData) => {
    setSaving(true);
    try {
      if (modalMode === 'create' || modalMode === 'duplicate') {
        // Check if sessionData is an array (multiple sessions) or single session
        if (Array.isArray(sessionData)) {
          // Create multiple sessions with bulk creation to assign session_group_id
          try {
            await adminAPI.createSession(sessionData); // Send array for bulk creation
            toast.success(`${sessionData.length} séances créées avec succès`);
          } catch (error) {
            console.error('Error creating sessions:', error);
            const errorMsg = error.response?.data?.non_field_errors?.[0] ||
                            error.response?.data?.detail ||
                            error.message ||
                            'Erreur lors de la création des séances';
            toast.error(errorMsg);
          }
        } else {
          // Create single session
          await adminAPI.createSession(sessionData);
          toast.success('Séance créée avec succès');
        }
        // Reload all sessions to get complete data with coach and location details
        const allSessions = await adminAPI.getSessions();
        setSessions(Array.isArray(allSessions) ? allSessions : []);
      } else if (modalMode === 'edit') {
        const response = await adminAPI.updateSession(selectedSession.id, sessionData);

        // Check if this was a group update
        if (sessionData.update_group && Array.isArray(response)) {
          toast.success(`${response.length} séances du groupe mises à jour avec succès`);
        } else {
          toast.success('Séance mise à jour avec succès');
        }

        // Reload all sessions to get complete data with coach and location details
        const allSessions = await adminAPI.getSessions();
        setSessions(Array.isArray(allSessions) ? allSessions : []);
      }
      setShowModal(false);
      setSelectedSession(null);
    } catch (error) {
      console.error('Error saving session:', error);
      toast.error('Erreur lors de la sauvegarde de la séance');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (session) => {
    if (!session) return '';

    if (session.current_bookings >= session.max_capacity) {
      return 'bg-red-100 text-red-800 border-red-200';
    } else if (session.current_bookings > 0) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else {
      return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const getStatusText = (session) => {
    if (!session) return '';

    if (session.current_bookings >= session.max_capacity) {
      return 'Complet';
    } else if (session.current_bookings > 0) {
      return `${session.current_bookings}/${session.max_capacity}`;
    } else {
      return 'Disponible';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Planning</h2>
          <p className="text-gray-600">Gérez les créneaux et disponibilités</p>
        </div>
        
        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['day', 'week'].map((viewType) => (
              <button
                key={viewType}
                onClick={() => handleViewChange(viewType)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  view === viewType
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {viewType === 'day' ? 'Jour' : 'Semaine'}
              </button>
            ))}
          </div>
          
          {/* Date Navigation */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                const newDate = new Date(currentDate);
                newDate.setDate(newDate.getDate() - (view === 'day' ? 1 : 7));
                handleDateChange(newDate);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            
            <span className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
              {view === 'week' 
                ? `${getWeekDays()[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${getWeekDays()[6].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`
                : currentDate.toLocaleDateString('fr-FR', { 
                    weekday: view === 'day' ? 'long' : undefined,
                    year: 'numeric', 
                    month: 'long', 
                    day: view === 'day' ? 'numeric' : undefined 
                  })
              }
            </span>
            
            <button
              onClick={() => {
                const newDate = new Date(currentDate);
                newDate.setDate(newDate.getDate() + (view === 'day' ? 1 : 7));
                handleDateChange(newDate);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Schedule Grid */}
      <div className="card overflow-hidden">
        {view === 'week' && (
          <div className="overflow-x-auto">
            <div className="min-w-[1200px] lg:min-w-[800px]">
              {/* Header */}
              <div className="grid grid-cols-8 border-b border-gray-200">
                <div className="p-2 sm:p-4 bg-gray-50 font-medium text-gray-900 text-xs sm:text-sm">Heure</div>
                {getWeekDays().map((day, index) => (
                  <div key={index} className="p-2 sm:p-4 bg-gray-50 text-center">
                    <div className="font-medium text-gray-900 text-xs sm:text-sm">
                      {day.toLocaleDateString('fr-FR', { weekday: 'short' })}
                    </div>
                    <div className="text-xs text-gray-500">
                      {day.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Time Slots */}
              {timeSlots.map((time) => (
                <div key={time} className="grid grid-cols-8 border-b border-gray-100">
                  <div className="p-2 sm:p-4 bg-gray-50 font-medium text-gray-700 flex items-center">
                    <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="text-xs sm:text-sm">{time}</span>
                  </div>
                  {getWeekDays().map((day, dayIndex) => {
                    const sessionsInSlot = getSessionsForSlot(day, time);
                    return (
                      <div key={dayIndex} className="p-1 sm:p-2 border-r border-gray-100 min-h-[60px] sm:min-h-[80px] relative group">
                        {sessionsInSlot.length > 0 ? (
                          <div className="space-y-1">
                            {sessionsInSlot.map((session) => (
                              <div
                                key={session.id}
                                className={`p-1 sm:p-2 rounded-lg border text-xs ${getStatusColor(session)} relative cursor-pointer hover:shadow-md transition-shadow`}
                                onClick={() => handleViewSessionDetails(session)}
                              >
                                {session.title && (
                                  <div className="font-bold text-xs mb-1 truncate">{session.title}</div>
                                )}
                                <div className="font-medium text-xs truncate">{session.coach?.user?.full_name}</div>
                                <div className="text-xs opacity-75 truncate">{session.location?.name}</div>
                                <div className="text-xs opacity-75">{getStatusText(session)}</div>

                                {/* Action buttons - show on hover */}
                                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                                  {!isTimeSlotInPast(day, time) ? (
                                    <>
                                      <button
                                        onClick={(e) => handleEditSession(e, session)}
                                        className="p-1 bg-white rounded shadow hover:bg-gray-50"
                                        title="Modifier"
                                      >
                                        <PencilIcon className="h-3 w-3 text-gray-600" />
                                      </button>
                                      {/* <button
                                        onClick={() => handleDuplicateSession(session)}
                                        className="p-1 bg-white rounded shadow hover:bg-gray-50"
                                        title="Dupliquer"
                                      >
                                        <DocumentDuplicateIcon className="h-3 w-3 text-gray-600" />
                                      </button> */}
                                      <button
                                        onClick={(e) => handleDeleteSession(e, session.id)}
                                        className="p-1 bg-white rounded shadow hover:bg-red-50"
                                        title="Supprimer"
                                      >
                                        <TrashIcon className="h-3 w-3 text-red-600" />
                                      </button>
                                    </>
                                  ) : (
                                    <div className="p-1 bg-gray-100 rounded shadow">
                                      <span className="text-xs text-gray-500">Passé</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                            ))}
                            {!isTimeSlotInPast(day, time) && (
                              <button
                                onClick={() => handleCreateSession(day, time)}
                                className="w-full mt-1 p-1 hover:bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 hover:border-gray-300 transition-colors flex items-center justify-center"
                              >
                                <PlusIcon className="h-3 w-3 text-gray-400" />
                              </button>
                            )}
                          </div>
                        ) : (
                          !isTimeSlotInPast(day, time) ? (
                            <button
                              onClick={() => handleCreateSession(day, time)}
                              className="w-full h-full hover:bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 hover:border-gray-300 transition-colors flex items-center justify-center"
                            >
                              <PlusIcon className="h-4 w-4 text-gray-400" />
                            </button>
                          ) : (
                            <div className="w-full h-full rounded-lg border-2 border-dashed border-gray-100 flex items-center justify-center">
                              <span className="text-xs text-gray-400">Non disponible</span>
                            </div>
                          )
                        )}
                        
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {view === 'day' && (
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {currentDate.toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>

            {timeSlots.map((time) => {
              const sessionsInSlot = getSessionsForSlot(currentDate, time);
              return (
                <div key={time} className="flex flex-col sm:flex-row sm:items-center p-3 sm:p-4 border border-gray-200 rounded-lg group">
                  <div className="w-full sm:w-20 font-medium text-gray-700 flex items-center mb-2 sm:mb-0">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    {time}
                  </div>
                  <div className="flex-1 sm:ml-4">
                    {sessionsInSlot.length > 0 ? (
                      <div className="space-y-2">
                        {sessionsInSlot.map((session) => (
                          <div
                            key={session.id}
                            className={`p-3 rounded-lg border ${getStatusColor(session)} relative cursor-pointer hover:shadow-md transition-shadow`}
                            onClick={() => handleViewSessionDetails(session)}
                          >
                            {session.title && (
                              <div className="font-bold text-sm mb-2">{session.title}</div>
                            )}
                            <div className="font-medium">{session.coach?.user?.full_name}</div>
                            <div className="text-sm opacity-75">{session.location?.name} • {getStatusText(session)}</div>

                            {/* Action buttons */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                              {!isTimeSlotInPast(currentDate, time) ? (
                                <>
                                  <button
                                    onClick={(e) => handleEditSession(e, session)}
                                    className="p-1 bg-white rounded shadow hover:bg-gray-50"
                                    title="Modifier"
                                  >
                                    <PencilIcon className="h-3 w-3 text-gray-600" />
                                  </button>

                                  <button
                                    onClick={(e) => handleDeleteSession(e, session.id)}
                                    className="p-1 bg-white rounded shadow hover:bg-red-50"
                                    title="Supprimer"
                                  >
                                    <TrashIcon className="h-3 w-3 text-red-600" />
                                  </button>
                                </>
                              ) : (
                                <div className="p-1 bg-gray-100 rounded shadow">
                                  <span className="text-xs text-gray-500">Passé</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {!isTimeSlotInPast(currentDate, time) && (
                          <button
                            onClick={() => handleCreateSession(currentDate, time)}
                            className="w-full mt-2 p-2 hover:bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 hover:border-gray-300 transition-colors text-left flex items-center"
                          >
                            <PlusIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-gray-400 text-sm">Ajouter une séance</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      !isTimeSlotInPast(currentDate, time) ? (
                        <button
                          onClick={() => handleCreateSession(currentDate, time)}
                          className="w-full p-3 hover:bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 hover:border-gray-300 transition-colors text-left flex items-center"
                        >
                          <PlusIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-400">Créneau disponible - Cliquer pour créer une séance</span>
                        </button>
                      ) : (
                        <div className="w-full p-3 rounded-lg border-2 border-dashed border-gray-100 text-left flex items-center">
                          <span className="text-gray-400">Créneau non disponible (passé)</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}


      </div>

      {/* Legend */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Légende</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-100 border border-green-200 rounded mr-2"></div>
            <span className="text-sm text-gray-700">Disponible</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded mr-2"></div>
            <span className="text-sm text-gray-700">Partiellement réservé</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-100 border border-red-200 rounded mr-2"></div>
            <span className="text-sm text-gray-700">Complet</span>
          </div>
        </div>
      </div>

      {/* Session Modal */}
      {showModal && (
        <SessionModal
          session={selectedSession}
          coaches={coaches}
          locations={locations}
          sessions={sessions}
          mode={modalMode}
          saving={saving}
          onSave={handleSaveSession}
          onClose={() => {
            setShowModal(false);
            setSelectedSession(null);
          }}
        />
      )}

      {/* Session Detail Modal */}
      <SessionDetailModal
        session={detailSession}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setDetailSession(null);
        }}
        onSessionUpdate={fetchData}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
      />
    </div>
  );
};

// Session Modal Component
const SessionModal = ({ session, coaches, locations, mode, saving, onSave, onClose, sessions }) => {
  const [validationErrors, setValidationErrors] = useState([]);
  const [formData, setFormData] = useState({
    title: session?.title || '',
    date: session?.date || '',
    start_time: session?.start_time?.slice(0, 5) || '', // Remove seconds
    end_time: session?.end_time?.slice(0, 5) || '',
    coach: session?.coach?.id || '',
    location: session?.location?.id || '',
    max_capacity: session?.max_capacity || 1,
    status: session?.status || 'available'
  });

  // Function to scroll to top of modal when validation errors occur
  const scrollToTop = () => {
    setTimeout(() => {
      const modalElement = document.querySelector('.fixed.inset-0 .bg-white');
      if (modalElement) {
        modalElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100); // Small delay to ensure DOM is updated
  };

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });

  // Enhanced form data for multi-date and multi-time
  const [enhancedMode, setEnhancedMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState(session?.date ? [new Date(session.date)] : []);
  const [timeSlots, setTimeSlots] = useState([{
    start_time: session?.start_time?.slice(0, 5) || '',
    end_time: session?.end_time?.slice(0, 5) || ''
  }]);

  // Group update option for edit mode
  const [updateGroup, setUpdateGroup] = useState(false);
  const hasGroup = session?.session_group_id && session?.totalGroupSessions > 1;

  // Check for conflicts
  const getCoachConflicts = (coachId, date, startTime) => {
    if (!coachId || !date || !startTime) return [];

    return sessions.filter(s =>
      s.coach.id === coachId &&
      s.date === date &&
      s.start_time === startTime + ':00' &&
      s.id !== session?.id // Exclude current session when editing
    );
  };

  const getLocationConflicts = (locationId, date, startTime) => {
    if (!locationId || !date || !startTime) return [];

    return sessions.filter(s =>
      s.location.id === locationId &&
      s.date === date &&
      s.start_time === startTime + ':00' &&
      s.id !== session?.id // Exclude current session when editing
    );
  };

  // Helper functions for enhanced mode
  const formatDateForAPI = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const addTimeSlot = () => {
    setTimeSlots([...timeSlots, { start_time: '', end_time: '' }]);
  };

  const removeTimeSlot = (index) => {
    if (timeSlots.length > 1) {
      setTimeSlots(timeSlots.filter((_, i) => i !== index));
    }
  };

  const updateTimeSlot = (index, field, value) => {
    const newSlots = [...timeSlots];
    newSlots[index][field] = value;
    setTimeSlots(newSlots);
  };

  // Generate 1-hour sessions from time range
  const generateHourlySlots = (startTime, endTime) => {
    const slots = [];
    const start = parseInt(startTime.split(':')[0]);
    const end = parseInt(endTime.split(':')[0]);

    for (let hour = start; hour < end; hour++) {
      const startHour = hour.toString().padStart(2, '0') + ':00';
      const endHour = (hour + 1).toString().padStart(2, '0') + ':00';
      slots.push({ start_time: startHour, end_time: endHour });
    }

    return slots;
  };

  // Note: Real-time conflict checking is now handled by the backend API
  // This local function is kept only for preview purposes in enhanced mode
  const checkForConflicts = (sessionsToCreate) => {
    const conflicts = [];

    sessionsToCreate.forEach((newSession, index) => {
      const coachName = coaches.find(c => c.id === parseInt(newSession.coach))?.user?.full_name || 'Coach';
      const locationName = locations.find(l => l.id === parseInt(newSession.location))?.name || 'Studio';

      // 1. Check against existing sessions - Coach conflicts (same coach, same time, ANY location)
      const existingCoachConflict = sessions.find(existing =>
        existing.coach?.id === parseInt(newSession.coach) &&
        existing.date === newSession.date &&
        existing.start_time === newSession.start_time
      );

      if (existingCoachConflict) {
        const existingLocationName = existingCoachConflict.location?.name || 'Studio';
        conflicts.push({
          index,
          type: 'existing_coach',
          message: `❌ ${coachName} est déjà occupé le ${newSession.date} à ${newSession.start_time.slice(0, 5)} au ${existingLocationName}`
        });
      }

      // 2. Check against existing sessions - Location conflicts (same location, same time, ANY coach)
      const existingLocationConflict = sessions.find(existing =>
        existing.location?.id === parseInt(newSession.location) &&
        existing.date === newSession.date &&
        existing.start_time === newSession.start_time
      );

      if (existingLocationConflict) {
        const existingCoachName = existingLocationConflict.coach?.user?.full_name || 'Coach';
        conflicts.push({
          index,
          type: 'existing_location',
          message: `❌ ${locationName} est déjà occupé le ${newSession.date} à ${newSession.start_time.slice(0, 5)} par ${existingCoachName}`
        });
      }

      // 3. Check against other sessions in the same batch - Coach conflicts
      sessionsToCreate.forEach((otherSession, otherIndex) => {
        if (index !== otherIndex) {
          // Same coach cannot be in multiple places at the same time
          if (newSession.coach === otherSession.coach &&
              newSession.date === otherSession.date &&
              newSession.start_time === otherSession.start_time) {

            const otherLocationName = locations.find(l => l.id === parseInt(otherSession.location))?.name || 'Studio';
            conflicts.push({
              index,
              type: 'batch_coach',
              message: `❌ ${coachName} ne peut pas être dans 2 studios en même temps: ${locationName} et ${otherLocationName} le ${newSession.date} à ${newSession.start_time.slice(0, 5)}`
            });
          }

          // Same location cannot have multiple sessions at the same time
          if (newSession.location === otherSession.location &&
              newSession.date === otherSession.date &&
              newSession.start_time === otherSession.start_time) {

            const otherCoachName = coaches.find(c => c.id === parseInt(otherSession.coach))?.user?.full_name || 'Coach';
            conflicts.push({
              index,
              type: 'batch_location',
              message: `❌ ${locationName} ne peut pas avoir 2 séances en même temps: ${coachName} et ${otherCoachName} le ${newSession.date} à ${newSession.start_time.slice(0, 5)}`
            });
          }
        }
      });
    });

    // Remove duplicate conflicts
    const uniqueConflicts = conflicts.filter((conflict, index, self) =>
      index === self.findIndex(c => c.message === conflict.message)
    );

    return uniqueConflicts;
  };

  // Clear validation errors when form data changes
  const handleFormDataChange = (newFormData) => {
    setFormData(newFormData);
    if (validationErrors.length > 0) {
      setValidationErrors([]); // Clear errors when user makes changes
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();


    if (enhancedMode) {
      // Enhanced mode: create multiple sessions
      const sessionsToCreate = [];

      // For each selected date
      selectedDates.forEach(date => {
        if (!date) return;

        const dateStr = formatDateForAPI(date);

        // For each time slot
        timeSlots.forEach(slot => {
          if (!slot.start_time || !slot.end_time) return;

          // Generate hourly sessions from the time range
          const hourlySlots = generateHourlySlots(slot.start_time, slot.end_time);

          hourlySlots.forEach(hourlySlot => {
            // Check if time slot is in the past
            const now = new Date();
            const slotDateTime = new Date(`${dateStr}T${hourlySlot.start_time}`);

            if (slotDateTime >= now) {
              sessionsToCreate.push({
                ...formData,
                date: dateStr,
                start_time: hourlySlot.start_time,
                end_time: hourlySlot.end_time
              });
            }
          });
        });
      });

      // Validate sessions with backend API
      if (sessionsToCreate.length > 0) {
        try {
          // Call backend validation API
          await adminAPI.validateSessionsBulk(sessionsToCreate);

          // If validation passes, clear errors and save
          setValidationErrors([]);
          onSave(sessionsToCreate);
        } catch (error) {
          // Handle validation errors from backend
          if (error.conflicts) {
            // Format conflicts for display
            const formattedErrors = error.conflicts.map(conflict => ({
              type: conflict.type,
              message: conflict.message,
              details: conflict.details
            }));
            setValidationErrors(formattedErrors);
            scrollToTop(); // Scroll to top to show errors
          } else {
            // Handle other errors
            setValidationErrors([{
              type: 'api_error',
              message: error.message || 'Erreur lors de la validation des séances.'
            }]);
            scrollToTop(); // Scroll to top to show errors
          }
          return; // Don't close modal, don't save
        }
      } else {
        setValidationErrors([{
          type: 'no_sessions',
          message: 'Aucune séance valide à créer (toutes les créneaux sont dans le passé).'
        }]);
        scrollToTop(); // Scroll to top to show errors
      }
    } else {
      // Standard mode: single session (but split into 1-hour blocks)
      // Check if time slot is in the past
      const now = new Date();
      const slotDateTime = new Date(`${formData.date}T${formData.start_time}:00`);

      if (slotDateTime < now) {
        alert('Impossible de créer ou modifier une séance dans le passé.');
        return;
      }

      // Split the session into 1-hour blocks
      const hourlySlots = generateHourlySlots(formData.start_time, formData.end_time);
      const sessionsToCreate = [];

      hourlySlots.forEach(hourlySlot => {
        // Check if time slot is in the past
        const slotDateTime = new Date(`${formData.date}T${hourlySlot.start_time}`);
        if (slotDateTime >= now) {
          sessionsToCreate.push({
            ...formData,
            start_time: hourlySlot.start_time,
            end_time: hourlySlot.end_time
          });
        }
      });

      if (sessionsToCreate.length === 0) {
        setValidationErrors([{
          type: 'no_sessions',
          message: 'Aucune séance valide à créer (toutes les créneaux sont dans le passé).'
        }]);
        scrollToTop();
        return;
      }

      // Skip bulk validation for edit mode - let backend handle validation
      if (mode === 'edit') {
        // For edits, directly save without bulk validation
        // Backend will handle smart validation (only check conflicts if scheduling fields changed)
        // Pass the first (and only) session object, not the array, with updateGroup flag
        const sessionData = { ...sessionsToCreate[0], update_group: updateGroup };
        onSave(sessionData);
      } else {
        // For create/duplicate modes, run bulk validation
        try {
          await adminAPI.validateSessionsBulk(sessionsToCreate);
          // If validation passes, save all sessions
          onSave(sessionsToCreate);
        } catch (error) {
          // Handle validation errors
          if (error.conflicts) {
            const formattedErrors = error.conflicts.map(conflict => ({
              type: conflict.type,
              message: conflict.message,
              details: conflict.details
            }));
            setValidationErrors(formattedErrors);
            scrollToTop(); // Scroll to top to show errors
          } else {
            setValidationErrors([{
              type: 'api_error',
              message: error.message || 'Erreur lors de la validation de la séance.'
            }]);
            scrollToTop(); // Scroll to top to show errors
          }
          return; // Don't close modal, don't save
        }
      }
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case 'create': return 'Créer une séance';
      case 'edit': return 'Modifier la séance';
      case 'duplicate': return 'Dupliquer la séance';
      default: return 'Séance';
    }
  };



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg p-6 w-full ${enhancedMode ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`}>
        <h3 className="text-lg font-semibold mb-4">{getModalTitle()}</h3>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center mb-2">
              <svg className="h-5 w-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <h4 className="text-sm font-medium text-red-800">
                Erreurs de validation ({validationErrors.length})
              </h4>
            </div>
            <div className="space-y-2">
              {validationErrors.map((error, index) => (
                <div key={index} className="text-sm text-red-700">
                  <p className="font-medium">{error.message}</p>
                  {error.details && (error.details.date || error.details.start_time) && (
                    <p className="text-xs text-red-600 mt-1">
                      {error.details.date && `Date: ${error.details.date}`}
                      {error.details.date && error.details.start_time && ' - '}
                      {error.details.start_time && `Heure: ${error.details.start_time}`}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-red-600 mt-2">
              Veuillez corriger ces erreurs avant de sauvegarder.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre de la séance</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleFormDataChange({ ...formData, title: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Ex: Pilates Débutant, Fitness Avancé..."
            />
          </div>

          {/* Enhanced Mode Toggle */}
          {(mode === 'create' || mode === 'duplicate') && (
            <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
              <input
                type="checkbox"
                id="enhancedMode"
                checked={enhancedMode}
                onChange={(e) => setEnhancedMode(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="enhancedMode" className="text-sm font-medium text-gray-700">
                Mode avancé (multi-dates et créneaux multiples)
              </label>
            </div>
          )}

          {/* Group Update Toggle for Edit Mode */}
          {mode === 'edit' && hasGroup && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <input
                  type="checkbox"
                  id="updateGroup"
                  checked={updateGroup}
                  onChange={(e) => setUpdateGroup(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="updateGroup" className="text-sm font-medium text-gray-700">
                  Mettre à jour cette séance et les séances futures du groupe ({session?.updatableGroupSessions} séances)
                </label>
              </div>
              <p className="text-xs text-yellow-700">
                Cette séance fait partie d'un groupe de {session?.totalGroupSessions} séances créées ensemble.
                Cochez cette option pour appliquer les modifications à cette séance et aux {Math.max(0, (session?.updatableGroupSessions || 1) - 1)} séances futures du groupe (les séances passées ne seront pas modifiées).
              </p>
              {session?.groupSessions && (
                <div className="mt-2">
                  <p className="text-xs text-yellow-700 font-medium">Dates du groupe:</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {session.groupSessions.map((groupSession, index) => {
                      // Determine if this session will be updated
                      const currentSessionDate = new Date(session.date);
                      const currentSessionTime = session.start_time;
                      const groupSessionDate = new Date(groupSession.date);
                      const groupSessionTime = groupSession.start_time;

                      // Check if this session is current or future (will be updated)
                      const willBeUpdated = groupSessionDate > currentSessionDate ||
                        (groupSessionDate.getTime() === currentSessionDate.getTime() &&
                         groupSessionTime >= currentSessionTime);

                      return (
                        <span
                          key={index}
                          className={`inline-block px-2 py-1 text-xs rounded ${
                            willBeUpdated
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                          title={willBeUpdated ? 'Sera mise à jour' : 'Ne sera pas mise à jour (passée)'}
                        >
                          {new Date(groupSession.date).toLocaleDateString('fr-FR')}
                          {willBeUpdated && ' ✓'}
                        </span>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    <span className="inline-block w-2 h-2 bg-green-100 rounded mr-1"></span>
                    Sera mise à jour
                    <span className="inline-block w-2 h-2 bg-gray-100 rounded mr-1 ml-3"></span>
                    Ne sera pas mise à jour
                  </p>
                </div>
              )}
            </div>
          )}

          {enhancedMode ? (
            <>
              {/* Multi-Date Calendar Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sélectionner les dates
                </label>
                <div className="border border-gray-300 rounded-lg p-4 bg-white">
                  <Calendar
                    onChange={() => {}} // Disable default onChange
                    value={null} // Don't use default value
                    selectRange={false}
                    minDate={new Date()}
                    className="w-full border-0"
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        // Check if this date is selected
                        const isSelected = selectedDates.some(selectedDate =>
                          selectedDate &&
                          selectedDate.toDateString() === date.toDateString()
                        );

                        // Check if date is in the past
                        const isPast = date < new Date().setHours(0, 0, 0, 0);

                        if (isPast) {
                          return 'react-calendar__tile--disabled';
                        }

                        return isSelected ? 'react-calendar__tile--selected' : '';
                      }
                      return '';
                    }}
                    tileDisabled={({ date, view }) => {
                      if (view === 'month') {
                        // Disable past dates
                        return date < new Date().setHours(0, 0, 0, 0);
                      }
                      return false;
                    }}
                    onClickDay={(date) => {
                      // Only allow future dates
                      if (date < new Date().setHours(0, 0, 0, 0)) {
                        return;
                      }

                      // Toggle date selection
                      const isSelected = selectedDates.some(selectedDate =>
                        selectedDate &&
                        selectedDate.toDateString() === date.toDateString()
                      );

                      if (isSelected) {
                        // Remove date
                        setSelectedDates(selectedDates.filter(selectedDate =>
                          !selectedDate || selectedDate.toDateString() !== date.toDateString()
                        ));
                      } else {
                        // Add date
                        setSelectedDates([...selectedDates, date]);
                      }
                    }}
                  />
                </div>
                {selectedDates.length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium">
                      {selectedDates.length} date(s) sélectionnée(s):
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedDates.map((date, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {date.toLocaleDateString('fr-FR')}
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedDates(selectedDates.filter((_, i) => i !== index));
                            }}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Multi-Time Slots */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Créneaux horaires</label>
                <div className="space-y-2">
                  {timeSlots.map((slot, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="time"
                        value={slot.start_time}
                        onChange={(e) => updateTimeSlot(index, 'start_time', e.target.value)}
                        className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                      <span className="text-gray-500">à</span>
                      <input
                        type="time"
                        value={slot.end_time}
                        onChange={(e) => updateTimeSlot(index, 'end_time', e.target.value)}
                        className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                      {timeSlots.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTimeSlot(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addTimeSlot}
                    className="w-full p-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600"
                  >
                    + Ajouter un créneau
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Les créneaux seront automatiquement divisés en sessions d'1 heure
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Standard Single Date/Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
            </>
          )}

          {!enhancedMode && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heure début</label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heure fin</label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          )}

          {/* Time slot warning */}
          {formData.date && formData.start_time && (
            (() => {
              const now = new Date();
              const slotDateTime = new Date(`${formData.date}T${formData.start_time}:00`);

              if (slotDateTime < now) {
                return (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-red-800 font-medium">
                        ⚠️ Cette heure est dans le passé. Impossible de créer ou modifier cette séance.
                      </span>
                    </div>
                  </div>
                );
              }
              return null;
            })()
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Coach</label>
            <select
              value={formData.coach}
              onChange={(e) => handleFormDataChange({ ...formData, coach: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="">Sélectionner un coach</option>
              {coaches.map(coach => (
                <option key={coach.id} value={coach.id}>
                  {coach.user.full_name}
                </option>
              ))}
            </select>

            {/* Coach conflict warning */}
            {formData.coach && formData.date && formData.start_time && (
              (() => {
                const coachConflicts = getCoachConflicts(formData.coach, formData.date, formData.start_time);
                if (coachConflicts.length > 0) {
                  return (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="h-4 w-4 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-yellow-800">
                          Ce coach a déjà une séance à {formData.start_time} dans {coachConflicts[0].location.name}
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Studio</label>
            <select
              value={formData.location}
              onChange={(e) => handleFormDataChange({ ...formData, location: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="">Sélectionner un studio</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>

            {/* Location conflict warning */}
            {formData.location && formData.date && formData.start_time && (
              (() => {
                const locationConflicts = getLocationConflicts(formData.location, formData.date, formData.start_time);
                if (locationConflicts.length > 0) {
                  return (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="h-4 w-4 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-red-800">
                          Ce studio est déjà occupé à {formData.start_time} par {locationConflicts[0].coach.user.full_name}
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()
            )}
          </div>

          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Capacité max</label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.max_capacity}
              onChange={(e) => setFormData({ ...formData, max_capacity: parseInt(e.target.value) })}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div> */}

          {/* Preview for Enhanced Mode */}
          {enhancedMode && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Aperçu des séances à créer</h4>
              {(() => {
                const sessionsToCreate = [];
                const validDates = selectedDates.filter(date => date);
                const validTimeSlots = timeSlots.filter(slot => slot.start_time && slot.end_time);

                // Generate all sessions that would be created
                validDates.forEach(date => {
                  validTimeSlots.forEach(slot => {
                    const hourlySlots = generateHourlySlots(slot.start_time, slot.end_time);
                    const now = new Date();
                    hourlySlots.forEach(hourlySlot => {
                      const slotDateTime = new Date(`${date}T${hourlySlot.start_time}`);
                      if (slotDateTime >= now) {
                        sessionsToCreate.push({
                          ...formData,
                          date: date,
                          start_time: hourlySlot.start_time,
                          end_time: hourlySlot.end_time
                        });
                      }
                    });
                  });
                });

                // Check for conflicts
                const conflicts = formData.coach && formData.location ? checkForConflicts(sessionsToCreate) : [];

                return (
                  <div className="text-sm text-gray-600">
                    <p><strong>{sessionsToCreate.length}</strong> séances seront créées</p>
                    <p><strong>{validDates.length}</strong> dates sélectionnées</p>
                    <p><strong>{validTimeSlots.length}</strong> créneaux horaires</p>

                    {conflicts.length > 0 && (
                      <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded">
                        <p className="text-yellow-800 font-medium">⚠️ Conflits détectés:</p>
                        <ul className="text-yellow-700 text-xs mt-1">
                          {conflicts.slice(0, 3).map((conflict, index) => (
                            <li key={index}>• {conflict.message}</li>
                          ))}
                          {conflicts.length > 3 && (
                            <li>• ... et {conflicts.length - 3} autres conflits</li>
                          )}
                        </ul>
                      </div>
                    )}

                    {sessionsToCreate.length === 0 && (
                      <p className="text-red-600 mt-2">⚠️ Aucune séance valide (vérifiez les dates et heures)</p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Preview for Standard Mode */}
          {!enhancedMode && formData.start_time && formData.end_time && formData.date && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Aperçu des séances à créer</h4>
              {(() => {
                const hourlySlots = generateHourlySlots(formData.start_time, formData.end_time);
                const now = new Date();
                const validSlots = hourlySlots.filter(slot => {
                  const slotDateTime = new Date(`${formData.date}T${slot.start_time}`);
                  return slotDateTime >= now;
                });

                return (
                  <div className="text-sm text-gray-600">
                    <p><strong>{validSlots.length}</strong> séances seront créées (séances d'1 heure)</p>
                    <div className="mt-2 space-y-1">
                      {validSlots.map((slot, index) => (
                        <div key={index} className="text-xs bg-white p-2 rounded border">
                          📅 {formData.date} - ⏰ {slot.start_time} à {slot.end_time}
                        </div>
                      ))}
                    </div>
                    {validSlots.length === 0 && (
                      <p className="text-red-600 mt-2">⚠️ Aucune séance valide (créneaux dans le passé)</p>
                    )}
                    {validSlots.length !== hourlySlots.length && (
                      <p className="text-yellow-600 text-xs mt-2">
                        ⚠️ {hourlySlots.length - validSlots.length} créneaux ignorés (dans le passé)
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {saving ? 'En cours...' : (mode === 'edit' ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        loading={saving}
      />
    </div>
  );
};

export default Schedule;
